import { BrowserRouter as Router, Route, Link } from "react-router-dom";
import UnclaimedSystem from "../components/System";
import AssignSystemModal from "../components/AssignSystemModal";
import MultiViewModal from "../components/MultiViewModal";
import ClaimedSystem from "../components/ClaimedSystem";
import { Menu } from "lucide-react";
import Button from "react-bootstrap/Button";
import Image from "react-bootstrap/Image";
import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { useAuth } from "../components/Contexts/AuthContext";
import { useOnlineUsers } from "../components/Contexts/OnlineUsersContext"; // Import the context hook
import { useSocket } from "../components/Contexts/SocketContext"; // Import the socket context
import { useSystems } from "../components/Contexts/SystemsContext"; // Import the SystemsContext
import LiveStreamModal from "../components/LiveStreamModal";
import "./dashboard.css";

interface DashboardProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  unclaimSystem: (id: number) => void;
  selectedUser?: string | null;
  selectedUserName?: string | null;
  moveSystemToDashboard: (id: number) => void;
}

interface System {
  last_checked: number | null;
  note: string | null;
  id: number;
  system_name: string;
  patient_name: string;
  status: string;
  battery_lvl: number;
  charge_status: boolean;
  screenshot?: any;
  claimedby: string | null;
  ping?: string;
  network_type?: string;
  is_live_view?: string;
  last_seen_at?: string | number | null;
  ss_progress?: number;
  remote_session?: string;
}

Modal.setAppElement("#root");

const Dashboard: React.FC<DashboardProps> = ({
  isOpen,
  setIsOpen,
  unclaimSystem,
  selectedUser,
  selectedUserName,
  moveSystemToDashboard,
}) => {
  const { systems, setSystems } = useSystems();
  const [unclaimedSystems, setUnclaimedSystems] = useState<System[]>([]);
  const [claimedSystems, setClaimedSystems] = useState<System[]>([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalSystems, setModalSystems] = useState<any[]>([]); // Store multiple systems with screenshots
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [liveViewSystemId, setLiveViewSystemId] = useState<number | null>(null);
  const [notes, setNotes] = useState<{ [key: number]: string }>({}); // Store notes per system ID
  const { onlineUsers, setOnlineUsers } = useOnlineUsers(); // Get onlineUsers and setOnlineUsers from context
  const auth = useAuth(); // Assume this provides the logged-in user's ID and organizationId
  const { socket } = useSocket(); // Get the socket from context

  function handleLiveView(systemId: number) {
    setLiveViewSystemId(systemId);
  }

  // Multi-view state. `multiViewOpen` controls visibility. `multiViewSelected`
  // remembers which 4 systems were chosen so the picker can be skipped on
  // re-open. Take Control on a cell no longer closes the modal — multi-view
  // handles control internally by reflowing into focus mode.
  const [multiViewOpen, setMultiViewOpen] = useState(false);
  const [multiViewSelected, setMultiViewSelected] = useState<number[]>([]);

  const openMultiView = () => {
    setMultiViewSelected([]);
    setMultiViewOpen(true);
  };

  // Navbar's Multi-View button lives in App.tsx-rendered NavBar, so it
  // can't call openMultiView directly. Listen for a custom event instead.
  // Refuses to open when the operator is viewing another user's systems —
  // they can't monitor systems they haven't claimed themselves.
  useEffect(() => {
    const handler = () => {
      if (selectedUser && selectedUser !== auth.user?.sub) return;
      openMultiView();
    };
    window.addEventListener("open-multi-view", handler);
    return () => window.removeEventListener("open-multi-view", handler);
  }, [selectedUser, auth.user?.sub]);
  const closeMultiView = () => {
    setMultiViewOpen(false);
    setMultiViewSelected([]);
  };

  // Admin-only: open the user picker for a given system. Centralized here
  // so both ClaimedSystem (gear menu) and UnclaimedSystem (sidebar row)
  // share the same modal instance rather than duplicating it.
  const [assignSystemId, setAssignSystemId] = useState<number | null>(null);
  const openAssignModal = (systemId: number) => setAssignSystemId(systemId);
  const closeAssignModal = () => setAssignSystemId(null);
  const handleAssign = (systemId: number, userId: string) => {
    if (socket) socket.emit("assignSystem", { systemId, userId });
    setAssignSystemId(null);
  };
  const assignTargetSystem = assignSystemId !== null
    ? systems.find((s) => s.id === assignSystemId)
    : null;

  function removeFromPool(systemId: number, organizationId: string) {
    if (socket) {
      socket.emit("REMOVE_FROM_POOL", { systemId, organizationId });
    }
  }

  function restartProcess(systemId: number, organizationId: string) {
    if (socket) {
      socket.emit("RESTART_PROCESS", { systemId, organizationId });
    }
  }

  async function runCheck(systemId: number) {
    if (socket) {
      socket.emit("RUN_CHECK", {
        systemId: systemId,
        organizationId: auth.user?.organizationid,
        userId: auth.user?.sub,
      });

      setSystems((prevSystems) =>
        prevSystems.map((s) =>
          s.id === systemId ? { ...s, status: "RUN_CHECK" } : s
        )
      );
    }
  }

  function runCheckAll() {
    const eligible = claimedSystems.filter(
      (s) =>
        s.status !== "Offline" &&
        s.status !== "invalidHeadbox" &&
        s.status !== "neuroworksClosed" &&
        s.status !== "RUN_CHECK" &&
        s.status !== "running_bluetooth" &&
        s.status !== "taking_ss" &&
        s.status !== "awaiting_approval" &&
        s.status !== "writing_note" &&
        // Skip systems with an active 3rd-party remote session — interrupting
        // someone connected via TeamViewer or GoToMyPC would yank focus and
        // disrupt their work mid-session. Individual Run Check buttons on
        // these cards are already disabled in ClaimedSystem for the same
        // reason; the bulk action needs to mirror that.
        s.remote_session !== "teamviewer" &&
        s.remote_session !== "gotomypc"
    );
    eligible.forEach((s) => runCheck(s.id));
  }

  // Claim cap is now per-org (organization_settings.max_systems_per_user)
  // and enforced server-side. Frontend doesn't preflight — if a non-admin
  // exceeds their org's limit, backend rejects with `claim_failed` which
  // SystemsContext renders as an error toast. Trading a ~100ms toast lag
  // for not having a stale hardcoded "12" that drifts from admin's
  // configured value.
  const handleClaim = (id: number) => {
    moveSystemToDashboard(id);
  };

  // Approval-modal dismissal. The modal is condition-driven (any claimed
  // system in awaiting_approval with a screenshot pops it), so without a
  // dismissed list there is no way to close it — it would just reopen on
  // the next render. Dismissed ids are pruned once the system leaves
  // awaiting_approval so a future check on the same system pops the modal
  // again as expected.
  const [dismissedApprovals, setDismissedApprovals] = useState<number[]>([]);

  useEffect(() => {
    setDismissedApprovals((prev) =>
      prev.filter((id) =>
        systems.some((s) => s.id === id && s.status === "awaiting_approval")
      )
    );
  }, [systems]);

  const dismissApprovalModal = () => {
    setDismissedApprovals((prev) => [
      ...prev,
      ...modalSystems.map((s) => s.id),
    ]);
    setModalIsOpen(false);
  };

  useEffect(() => {
    const systemsWithScreenshots = systems.filter(
      (sys) =>
        sys.screenshot &&
        sys.claimedby === auth.user?.sub &&
        sys.status === "awaiting_approval" &&
        !dismissedApprovals.includes(sys.id)
    );

    setModalSystems(systemsWithScreenshots);
    setModalIsOpen(systemsWithScreenshots.length > 0 && !isOpen);
  }, [systems, auth.user, isOpen, dismissedApprovals]);

  useEffect(() => {
    if (auth.isAuthenticated && socket) {
      socket.emit("getOnlineUsers");

      socket.on("onlineUsers", (users: any[]) => {
        setOnlineUsers(users);
      });

      socket.on("disconnect", () => {
        setSystems([]);
      });

      return () => {
        socket.off("onlineUsers");
      };
    }
  }, [auth.isAuthenticated, socket, setOnlineUsers]);
  const sortSystems = <T extends { system_name: string }>(arr: T[]): T[] => {
    return [...arr].sort((a, b) => {
      const n = (s: string) => parseInt(s.match(/(\d+)\s*$/)?.[1] ?? "0");
      return n(a.system_name) - n(b.system_name);
    });
  };

  useEffect(() => {
    setUnclaimedSystems(sortSystems(systems.filter((sys) => !sys.claimedby)));
    setClaimedSystems(
      sortSystems(systems.filter(
        (sys) => sys.claimedby === (selectedUser || auth.user?.sub)
      ))
    );
  }, [systems, selectedUser, auth.user?.sub]);
  const handleImageClick = (imageUrl: string) => {
    setFullscreenImage(imageUrl);
  };

  const handleNoteChange = (systemId: number, value: string) => {
    setNotes((prevNotes) => ({
      ...prevNotes,
      [systemId]: value,
    }));
  };
  const handleApprove = (system: System) => {
    if (socket) {
      socket.emit("APPROVE_CHECK", {
        id: system.id,
        note: notes[system.id],
      });

      setSystems((prev) =>
        prev.map((s) =>
          s.id === system.id ? { ...s, status: "writing_note", screenshot: undefined } : s
        )
      );
      setModalSystems((prev) => prev.filter((s) => s.id !== system.id));
      if (modalSystems.length === 1) setModalIsOpen(false);
    }
  };

  const handleDeny = (system: System) => {
    if (socket) socket.emit("DENY_CHECK", { id: system.id });
    setSystems((prev) =>
      prev.map((s) =>
        s.id === system.id ? { ...s, status: "online", screenshot: undefined } : s
      )
    );
    setModalSystems((prev) => prev.filter((s) => s.id !== system.id));
    if (modalSystems.length === 1) setModalIsOpen(false);
  };

  const closeFullscreen = () => setFullscreenImage(null);
  return (
    <div className="dashboard-container">
      <div
        className={`overlay ${isOpen ? "active" : ""}`}
        onClick={() => setIsOpen(false)}
      />
      <div className={`sidebar ${isOpen ? "open" : ""}`}>
        <div className="top-div">
          <h2>Free Systems</h2>
        </div>
        {(() => {
          // Split into two buckets so the operator can scan "what's actively
          // monitoring a patient" separately from "what's idle and needs
          // setup." Patient name presence is the source of truth for "has
          // study" — the system app emits study_state, which updates this.
          const withStudy = unclaimedSystems.filter((s) => !!s.patient_name?.trim());
          const noStudy = unclaimedSystems.filter((s) => !s.patient_name?.trim());
          return (
            <>
              {withStudy.length > 0 && (
                <>
                  <div className="us-section-header">
                    Active Studies <span className="us-section-count">({withStudy.length})</span>
                  </div>
                  {withStudy.map((sys) => (
                    <UnclaimedSystem
                      key={sys.id}
                      moveSystemToDashboard={handleClaim}
                      {...sys}
                    />
                  ))}
                </>
              )}
              {noStudy.length > 0 && (
                <>
                  <div className="us-section-header">
                    No Study <span className="us-section-count">({noStudy.length})</span>
                  </div>
                  {noStudy.map((sys) => (
                    <UnclaimedSystem
                      key={sys.id}
                      moveSystemToDashboard={handleClaim}
                      {...sys}
                    />
                  ))}
                </>
              )}
            </>
          );
        })()}
        {unclaimedSystems.length === 0 && (
          <main
            style={{
              textAlign: "center",
              left: "0",
              position: "absolute",
              top: "20%",
              width: "100%",
            }}
          >
            <Image src="noSystems.png" className="noFreeSystemsPic " rounded />
            <h1 style={{ fontSize: "2rem", fontWeight: "200" }}>
              No Free Systems
            </h1>
          </main>
        )}
      </div>
      {claimedSystems.length > 0 && !selectedUser && (
        <div className="systems-toolbar">
          <span className="systems-toolbar-count">
            {claimedSystems.length} {claimedSystems.length === 1 ? "system" : "systems"}
          </span>
          <button className="systems-toolbar-btn" onClick={runCheckAll}>
            ▶ Run Check All
          </button>
        </div>
      )}
      <div className="systems-grid">
        {claimedSystems.map((sys) => (
          <ClaimedSystem
            key={sys.id}
            id={sys.id}
            system_name={sys.system_name}
            patient_name={sys.patient_name}
            status={sys.status}
            claimedby={sys.claimedby}
            battery_lvl={sys.battery_lvl}
            charge_status={sys.charge_status}
            signalStrength={sys.ping ? parseInt(sys.ping) : 0}
            networkType={sys.network_type || "wifi"}
            last_checked={sys.last_checked}
            note={sys.note}
            organizationId={String(auth.user?.organizationid ?? "")}
            isViewingOthers={!!selectedUser}
            isLiveView={sys.is_live_view === "true"}
            lastSeenAt={sys.last_seen_at ?? null}
            ssProgress={sys.ss_progress}
            remoteSession={sys.remote_session || ""}
            unclaimSystem={unclaimSystem}
            runCheck={runCheck}
            removeFromPool={removeFromPool}
            restartProcess={restartProcess}
            onLiveView={handleLiveView}
            moveSystemToDashboard={moveSystemToDashboard}
            onAssign={openAssignModal}
          />
        ))}
      </div>

      {claimedSystems.length === 0 && !selectedUser && (
        <main
          className={`content ${isOpen ? "dimmed" : ""}`}
          style={{
            textAlign: "center",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none"
          }}
        >
          <img src="noSystems.png" style={{ width: "300px", maxWidth: "25vw" }}></img>
          <h1 style={{ fontSize: "2rem", margin: "20px 0 10px 0" }}>No Systems Selected</h1>
          <p style={{ margin: "0", color: "#666" }}>Select systems from the top left menu</p>
        </main>
      )}

      {claimedSystems.length === 0 && selectedUser && (
        <main
          className={`content ${isOpen ? "dimmed" : ""}`}
          style={{
            textAlign: "center",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none"
          }}
        >
          <h1 style={{ fontSize: "2rem", margin: "0" }}>{selectedUserName} has no systems</h1>
        </main>
      )}
      <>
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={dismissApprovalModal}
          className="custom-modal"
          overlayClassName="custom-overlay"
        >
          <div className="modal-content">
            <button
              aria-label="Dismiss"
              onClick={dismissApprovalModal}
              style={{
                position: "absolute",
                top: 10,
                right: 14,
                background: "none",
                border: "none",
                fontSize: "22px",
                lineHeight: 1,
                cursor: "pointer",
                color: "#666",
              }}
            >
              ×
            </button>
            {modalSystems.map((system) => (
              <div key={system.id} className="system-section">
                <div className="system-title">
                  <div>{system.system_name}</div>
                </div>
                <img
                  src={system.screenshot}
                  alt={`${system.system_name} Screenshot`}
                  className="screenshot"
                  onClick={() => handleImageClick(system.screenshot!)}
                  style={{ width: "100%", maxHeight: "55vh", objectFit: "contain", borderRadius: "10px", cursor: "pointer" }}
                />
                <div style={{ display: "flex", gap: "10px", marginTop: "12px", alignItems: "flex-start" }}>
                  <textarea
                    placeholder="Add a note (optional)"
                    value={notes[system.id] || ""}
                    onChange={(e) => handleNoteChange(system.id, e.target.value)}
                    className="note-input"
                    style={{ flex: 1, height: "80px", fontSize: "15px" }}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignSelf: "stretch" }}>
                    <Button
                      variant="success"
                      onClick={() => handleApprove(system)}
                      style={{ padding: "10px 24px", fontSize: "16px", fontWeight: 600, whiteSpace: "nowrap", flex: 1 }}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outline-danger"
                      onClick={() => handleDeny(system)}
                      style={{ padding: "6px 24px", fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap" }}
                    >
                      Deny
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Modal>

        <AssignSystemModal
          show={assignSystemId !== null}
          systemId={assignSystemId}
          systemName={assignTargetSystem?.system_name}
          currentClaimedBy={assignTargetSystem?.claimedby ?? null}
          onlineUsers={onlineUsers}
          onAssign={handleAssign}
          onClose={closeAssignModal}
        />

        {liveViewSystemId && (() => {
          const sys = systems.find(s => s.id === liveViewSystemId);
          return sys ? (
            <LiveStreamModal
              systemId={liveViewSystemId}
              systemName={sys.system_name}
              patientName={sys.patient_name}
              networkType={sys.network_type || "wifi"}
              ping={sys.ping ? parseInt(sys.ping) : 0}
              organizationId={String(auth.user?.organizationid ?? "")}
              onClose={() => {
                setLiveViewSystemId(null);
              }}
            />
          ) : null;
        })()}

        {multiViewOpen && (
          <MultiViewModal
            availableSystems={claimedSystems}
            organizationId={String(auth.user?.organizationid ?? "")}
            initialSelectedIds={multiViewSelected.length ? multiViewSelected : undefined}
            onClose={closeMultiView}
          />
        )}

        {fullscreenImage && (
          <div className="fullscreen-modal" onClick={closeFullscreen}>
            <img
              src={fullscreenImage}
              alt="Fullscreen Screenshot"
              className="fullscreen-image"
            />
          </div>
        )}
      </>
    </div>
  );
};

export default Dashboard;
